import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import keycloakConfig from "./keycloak-config";
import { ChakraProvider } from "@chakra-ui/react";
import customTheme from "./theme";
import DeviceLoginPage from "./components/DeviceLoginPage";
import { isEmbedded, EmbedAuthProvider, syncEmbedToken } from "./EmbedAuthProvider";
import {
  clearToken,
  setAccessTokenProvider,
  setToken,
} from "./Api";

// Suppress AbortError and CanceledError from development error overlay
// These are expected errors when navigating away from pages
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' &&
    (args[0].includes('AbortError') || 
     args[0].includes('CanceledError') ||
     args[0].includes('user aborted') ||
     args[0].includes('signal is aborted'))
  ) {
    // Silently ignore these expected errors
    return;
  }
  originalError.apply(console, args);
};

const root = ReactDOM.createRoot(document.getElementById("root"));

const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
const isDeviceLoginRoute = normalizedPath === "/device-login";

// Keep refresh-token handling inside keycloak-js. API requests ask this
// provider for a token with at least 30 seconds of validity; a 401 can request
// one forced refresh to cover background-tab timer races.
setAccessTokenProvider(async ({ forceRefresh = false } = {}) => {
  if (!keycloakConfig.authenticated) {
    return keycloakConfig.token || null;
  }

  await keycloakConfig.updateToken(forceRefresh ? -1 : 30);
  return keycloakConfig.token || null;
});

const handleKeycloakTokens = ({ token }) => {
  if (token) {
    setToken(token);
  } else {
    clearToken();
  }
};

// Embed-mode (PRD §7.3): when the iOS WKWebView injects a bearer token via
// `window.__ETHOSPHERE_TOKEN__` at document start, skip the Keycloak OAuth
// redirect entirely and render `<App />` directly. iOS already authenticated
// the user; we just consume the token.
const embedMode = !isDeviceLoginRoute && isEmbedded();
if (embedMode) {
  syncEmbedToken();
}

root.render(
  isDeviceLoginRoute ? (
    <ChakraProvider theme={customTheme}>
      <DeviceLoginPage />
    </ChakraProvider>
  ) : embedMode ? (
    <EmbedAuthProvider>
      <App />
    </EmbedAuthProvider>
  ) : (
    <ReactKeycloakProvider
      authClient={keycloakConfig}
      onTokens={handleKeycloakTokens}
      initOptions={{
        onLoad: "login-required",
        checkLoginIframe: false,
        // `ethosphere-client` is a public client — use PKCE (S256) for the
        // auth-code flow instead of the (inert) client secret we removed.
        pkceMethod: "S256",
      }}
    >
      <App />
    </ReactKeycloakProvider>
  )
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
